import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

export const OfferMediaDto = z
  .object({
    type: z.string(),
    product_category: z.string(),
    sanity_reference_id: z.string(),
    locale: z.string(),
  })
  .strict();
export type OfferMediaDto = z.infer<typeof OfferMediaDto>;

export const ProductDataDto = z
  .object({
    quantity: z.number(),
    productVariantId: z.string(),
    isOneTime: z.boolean().nullish(),
    isOnetime: z.boolean().nullish(),
  })
  .strict();
export type ProductDataDto = z.infer<typeof ProductDataDto>;

export const OfferSubscriptionTypeMediaDto = z
  .object({ sanity_reference_id: z.string(), type: z.string() })
  .strict();
export type OfferSubscriptionTypeMediaDto = z.infer<
  typeof OfferSubscriptionTypeMediaDto
>;

export const OfferSubscriptionTypeDto = z
  .object({
    id: z.number(),
    name: z.string(),
    locale: z.string(),
    product_data: z.array(ProductDataDto),
    product_category: z.string(),
    sort: z.number(),
    media: z.array(OfferSubscriptionTypeMediaDto),
  })
  .strict();
export type OfferSubscriptionTypeDto = z.infer<typeof OfferSubscriptionTypeDto>;

export const OfferSubscriptionAddonTypeDto = z
  .object({
    id: z.number(),
    name: z.string(),
    locale: z.string(),
    product_data: z.array(ProductDataDto),
    offer_subscription_type_id: z.number(),
  })
  .strict();
export type OfferSubscriptionAddonTypeDto = z.infer<
  typeof OfferSubscriptionAddonTypeDto
>;

export const OfferSubscriptionAddonDto = z
  .object({
    id: z.number(),
    offer_subscription_addon_type_id: z.number(),
    type: OfferSubscriptionAddonTypeDto.optional(),
  })
  .strict();
export type OfferSubscriptionAddonDto = z.infer<
  typeof OfferSubscriptionAddonDto
>;

export const OfferSubscriptionDto = z
  .object({
    id: z.number(),
    offer_subscription_type_id: z.number(),
    enable_flavors: z.boolean(),
    customization: z.object({}).partial().strict().passthrough(),
    type: OfferSubscriptionTypeDto.optional(),
    addons: z.array(OfferSubscriptionAddonDto),
    media: z.array(OfferSubscriptionTypeMediaDto),
  })
  .strict();
export type OfferSubscriptionDto = z.infer<typeof OfferSubscriptionDto>;

export const OfferRestrictionDto = z
  .object({
    created_at: z.string().datetime({ offset: true }),
    date_from: z.string().datetime({ offset: true }).nullable(),
    date_to: z.string().datetime({ offset: true }).nullable(),
    id: z.number(),
    is_sold_out: z.boolean(),
    locales: z.array(z.string()).nullable(),
    offer_id: z.number(),
    quantity: z.number().nullable(),
  })
  .strict();
export type OfferRestrictionDto = z.infer<typeof OfferRestrictionDto>;

export const OfferSubscriptionTypeDefaultsDto = z
  .object({
    created_at: z.string().datetime({ offset: true }).optional(),
    id: z.number().optional(),
    offer_id: z.number().optional(),
    pouches_default_subscription_type_id: z.number().nullable(),
    travel_pack_default_subscription_type_id: z.number().nullable(),
  })
  .strict();
export type OfferSubscriptionTypeDefaultsDto = z.infer<
  typeof OfferSubscriptionTypeDefaultsDto
>;

export const OfferWithAssociationsDto = z
  .object({
    add_same_subscription_options_to_rest: z.boolean().nullish(),
    created_at: z.string().datetime({ offset: true }),
    created_by_user_id: z.string(),
    headline: z.string(),
    hide_unselected_subscriptions_from_checkout: z.boolean(),
    id: z.number(),
    is_base_offer: z.boolean(),
    is_page_restriction_enabled: z.boolean(),
    is_paused: z.boolean(),
    name: z.string(),
    status: z.enum(["archived", "draft", "published"]),
    media: z.array(OfferMediaDto),
    subscriptions: z.array(OfferSubscriptionDto),
    restriction: z.array(OfferRestrictionDto),
    subscription_type_defaults: OfferSubscriptionTypeDefaultsDto.nullable(),
  })
  .strict();
export type OfferWithAssociationsDto = z.infer<typeof OfferWithAssociationsDto>;

export const OfferDataResponseDto = z
  .object({ data: OfferWithAssociationsDto })
  .strict();
export type OfferDataResponseDto = z.infer<typeof OfferDataResponseDto>;

export const OfferV2ResponseDto = z
  .object({ data: z.union([z.unknown(), z.unknown()]) })
  .strict();
export type OfferV2ResponseDto = z.infer<typeof OfferV2ResponseDto>;

export const OfferDataForPageDto = z
  .object({
    offer: OfferWithAssociationsDto,
    subscriptionTypes: z.array(OfferSubscriptionTypeDto),
    addonTypes: z.array(OfferSubscriptionAddonTypeDto),
    baseOffer: OfferWithAssociationsDto,
    baseOfferSubscriptionTypes: z.array(OfferSubscriptionTypeDto),
    baseOfferAddonTypes: z.array(OfferSubscriptionAddonTypeDto),
  })
  .strict();
export type OfferDataForPageDto = z.infer<typeof OfferDataForPageDto>;

export const OfferDataForPageResponseDto = z
  .object({ data: OfferDataForPageDto.nullable() })
  .strict();
export type OfferDataForPageResponseDto = z.infer<
  typeof OfferDataForPageResponseDto
>;

export const OfferDataForPageV2ResponseDto = z
  .object({ data: z.object({}).partial().strict() })
  .strict();
export type OfferDataForPageV2ResponseDto = z.infer<
  typeof OfferDataForPageV2ResponseDto
>;

export const ExperimentVariantPageResponseDto = z
  .object({
    id: z.number(),
    name: z.string(),
    index: z.number(),
    experiment_id: z.number(),
    page_id: z.number(),
    created_at: z.string().datetime({ offset: true }),
    statsig_group_id: z.string().nullable(),
    notes: z.string().nullable(),
  })
  .strict();
export type ExperimentVariantPageResponseDto = z.infer<
  typeof ExperimentVariantPageResponseDto
>;

export const ExperimentResponseDto = z
  .object({
    id: z.number(),
    status: z.string(),
    participating_users_percentage: z.number(),
    name: z.string(),
    hypothesis: z.string(),
    control_page_id: z.number(),
    decided_page_id: z.number().nullable(),
    created_at: z.string().datetime({ offset: true }),
    last_started_at: z.string().datetime({ offset: true }).nullable(),
    last_paused_at: z.string().datetime({ offset: true }).nullable(),
    abandoned_at: z.string().datetime({ offset: true }).nullable(),
    decided_at: z.string().datetime({ offset: true }).nullable(),
    created_by_user_id: z.string().nullable(),
    statsig_experiment_id: z.string().nullable(),
    test_type: z.string(),
    mab_success_event: z.string().nullable(),
    mab_event_value: z.string().nullable(),
    mab_exploration_window_seconds: z.number().nullable(),
    mab_attribution_window_seconds: z.number().nullable(),
    mab_winning_threshold_percent: z.number().nullable(),
  })
  .strict();
export type ExperimentResponseDto = z.infer<typeof ExperimentResponseDto>;

export const CustomContent = z
  .object({
    id: z.number(),
    created_at: z.string().datetime({ offset: true }),
    page_id: z.number().nullable(),
    version: z.number().nullable(),
    section_slug: z.string().nullable(),
    content_type: z.string().nullable(),
    is_current: z.boolean(),
    content: z.string().nullable(),
    locale: z.string(),
  })
  .strict();
export type CustomContent = z.infer<typeof CustomContent>;

export const CustomContentResponseDto = z
  .object({
    us: z.array(CustomContent),
    au: z.array(CustomContent),
    nz: z.array(CustomContent),
    de: z.array(CustomContent),
    gb: z.array(CustomContent),
    nl: z.array(CustomContent),
    se: z.array(CustomContent),
    eu: z.array(CustomContent),
    es: z.array(CustomContent),
    fr: z.array(CustomContent),
    jp: z.array(CustomContent),
    "apac-us": z.array(CustomContent),
  })
  .partial()
  .strict();
export type CustomContentResponseDto = z.infer<typeof CustomContentResponseDto>;

export const PageExperimentResponseDto = z
  .object({
    variant: ExperimentVariantPageResponseDto,
    experiment: ExperimentResponseDto,
    controlPageCustomContent: CustomContentResponseDto,
    pageCustomContent: CustomContentResponseDto,
  })
  .strict();
export type PageExperimentResponseDto = z.infer<
  typeof PageExperimentResponseDto
>;

export const PageExperimentResponseDataDto = z
  .object({ data: PageExperimentResponseDto })
  .strict();
export type PageExperimentResponseDataDto = z.infer<
  typeof PageExperimentResponseDataDto
>;

export const PageSearchResponseDto = z
  .object({ id: z.number(), name: z.string() })
  .strict();
export type PageSearchResponseDto = z.infer<typeof PageSearchResponseDto>;

export const PageSearchResponseDataDto = z
  .object({ data: z.array(PageSearchResponseDto) })
  .strict();
export type PageSearchResponseDataDto = z.infer<
  typeof PageSearchResponseDataDto
>;

export const SupabaseSectionResponseDto = z
  .object({
    id: z.string(),
    type: z.enum(["builtin", "custom", "static", "asset_library"]),
    name: z.string(),
  })
  .strict();
export type SupabaseSectionResponseDto = z.infer<
  typeof SupabaseSectionResponseDto
>;

export const CustomUrlsResponseDto = z
  .object({
    id: z.number(),
    page_id: z.number(),
    from: z.string(),
    to: z.string(),
  })
  .strict();
export type CustomUrlsResponseDto = z.infer<typeof CustomUrlsResponseDto>;

export const PageEditingUserRoleResponseDto = z
  .object({
    page_id: z.number().optional(),
    role: z.enum(["owner", "editor", "viewer"]),
    user_id: z.string(),
  })
  .strict();
export type PageEditingUserRoleResponseDto = z.infer<
  typeof PageEditingUserRoleResponseDto
>;

export const UserResponseDto = z
  .object({
    id: z.string(),
    email: z.string().nullable(),
    display_name: z.string().nullable(),
    avatar_url: z.string().nullable(),
    type: z.string(),
    created_at: z.string().datetime({ offset: true }),
  })
  .strict();
export type UserResponseDto = z.infer<typeof UserResponseDto>;

export const PageFullResponseDto = z
  .object({
    id: z.number(),
    name: z.string(),
    title: z.string(),
    sections2: z.array(SupabaseSectionResponseDto),
    pageUrl: z.string(),
    created_by: z.string(),
    status: z.enum(["draft", "published", "archived"]),
    created_at: z.string().datetime({ offset: true }),
    created_by_name: z.string(),
    created_by_user_id: z.string().nullable(),
    CustomUrls: CustomUrlsResponseDto.nullable(),
    page_editing_user_roles: z.array(PageEditingUserRoleResponseDto).nullable(),
    created_by_user: UserResponseDto.nullable(),
    parent_page_id: z.number().nullable(),
    is_experiment_variant: z.boolean(),
    frictionless: z.boolean(),
    is_hidden: z.boolean(),
    offer_id: z.number().nullable(),
    use_pdp: z.boolean().nullable(),
    pdp_url: z.string().nullable(),
    checkout_flow: z.object({}).partial().strict().passthrough().nullable(),
    totalSessions: z.number(),
    avgCvr: z.number(),
    totalCvrs: z.number(),
    bounceRate: z.number(),
  })
  .strict();
export type PageFullResponseDto = z.infer<typeof PageFullResponseDto>;

export const PageCreateResponseDto = z
  .object({
    page: PageFullResponseDto,
    customContent: z.object({}).partial().strict().passthrough(),
  })
  .strict();
export type PageCreateResponseDto = z.infer<typeof PageCreateResponseDto>;

export const PageCreateResponseDataDto = z
  .object({ data: PageCreateResponseDto })
  .strict();
export type PageCreateResponseDataDto = z.infer<
  typeof PageCreateResponseDataDto
>;

export const CreateCustomUrlRequestDto = z.object({ url: z.string() }).strict();
export type CreateCustomUrlRequestDto = z.infer<
  typeof CreateCustomUrlRequestDto
>;

export const CreateCustomUrlResponseDto = z
  .object({ url: z.string() })
  .strict();
export type CreateCustomUrlResponseDto = z.infer<
  typeof CreateCustomUrlResponseDto
>;

export const CreateCustomUrlDataResponseDto = z
  .object({ data: CreateCustomUrlResponseDto })
  .strict();
export type CreateCustomUrlDataResponseDto = z.infer<
  typeof CreateCustomUrlDataResponseDto
>;

export const SupabaseSectionRequestDto = z
  .object({
    id: z.string(),
    type: z.enum(["builtin", "custom", "static", "asset_library"]),
    name: z.string(),
  })
  .strict();
export type SupabaseSectionRequestDto = z.infer<
  typeof SupabaseSectionRequestDto
>;

export const CreateCustomContentPayloadDto = z
  .object({
    section_slug: z.string(),
    content_type: z.string(),
    content: z.string(),
    is_current: z.boolean(),
  })
  .strict();
export type CreateCustomContentPayloadDto = z.infer<
  typeof CreateCustomContentPayloadDto
>;

export const CustomContentDto = z
  .object({
    region: z.string(),
    content: z.array(CreateCustomContentPayloadDto),
  })
  .strict();
export type CustomContentDto = z.infer<typeof CustomContentDto>;

export const PageCreateRequestDto = z
  .object({
    name: z.string().nullable(),
    title: z.string().nullable(),
    createdAt: z.number(),
    status: z.enum(["draft", "published", "archived"]).nullable(),
    sections: z.array(SupabaseSectionRequestDto).nullable(),
    customContent: z.array(CustomContentDto).nullable(),
    frictionless: z.boolean().nullable(),
    offerId: z.number().nullable(),
    checkoutFlow: z.object({}).partial().strict().passthrough().nullable(),
    isHidden: z.boolean().nullable(),
    usePdp: z.boolean().nullable(),
    pdpUrl: z.string().nullable(),
  })
  .partial()
  .strict();
export type PageCreateRequestDto = z.infer<typeof PageCreateRequestDto>;

export const PageNameCheckResponseDto = z
  .object({ exists: z.boolean() })
  .strict();
export type PageNameCheckResponseDto = z.infer<typeof PageNameCheckResponseDto>;

export const PageNameCheckDataResponseDto = z
  .object({ data: PageNameCheckResponseDto })
  .strict();
export type PageNameCheckDataResponseDto = z.infer<
  typeof PageNameCheckDataResponseDto
>;

export const PageResponseDto = z
  .object({
    id: z.number(),
    parent_page_id: z.number().nullish(),
    title: z.string(),
    sections2: z.array(z.object({}).partial().strict().passthrough()),
    frictionless: z.boolean(),
    offer_id: z.number().nullish(),
    checkout_flow: z.object({}).partial().strict().passthrough().nullish(),
    status: z.enum(["draft", "published", "archived"]).nullish(),
    pdp_url: z.string().nullish(),
    use_pdp: z.boolean().nullish(),
  })
  .strict();
export type PageResponseDto = z.infer<typeof PageResponseDto>;

export const PageSeoTagsResponseDto = z
  .object({
    can_missing_locales_exist: z.boolean(),
    created_at: z.string(),
    description: z.string().nullish(),
    id: z.number(),
    internal_canonical: z.string().nullish(),
    is_no_index_page: z.boolean(),
    keywords: z.string().nullish(),
    open_graph_image_sanity_ref_id: z.string().nullish(),
    page_id: z.number(),
    structured_schema_data: z.array(
      z.object({}).partial().strict().passthrough()
    ),
    title: z.string(),
    url: z.string().nullish(),
  })
  .strict();
export type PageSeoTagsResponseDto = z.infer<typeof PageSeoTagsResponseDto>;

export const PageSeoTagsDataResponseDto = z
  .object({ data: PageSeoTagsResponseDto })
  .partial()
  .strict();
export type PageSeoTagsDataResponseDto = z.infer<
  typeof PageSeoTagsDataResponseDto
>;

export const PageEditingUserRoleWithUserResponseDto = z
  .object({
    page_id: z.number().optional(),
    role: z.enum(["owner", "editor", "viewer"]),
    user_id: z.string(),
    users: UserResponseDto,
  })
  .strict();
export type PageEditingUserRoleWithUserResponseDto = z.infer<
  typeof PageEditingUserRoleWithUserResponseDto
>;

export const PageRolesDataResponseDto = z
  .object({ data: z.array(PageEditingUserRoleWithUserResponseDto) })
  .strict();
export type PageRolesDataResponseDto = z.infer<typeof PageRolesDataResponseDto>;

export const PageAuditLogsResponseDto = z
  .object({
    created_at: z.string().datetime({ offset: true }),
    data: z.object({}).partial().strict().passthrough(),
    id: z.number(),
    page_id: z.number(),
    referenced_user_id: z.string().nullable(),
    type: z.enum([
      "create",
      "update",
      "role_add",
      "role_remove",
      "status_change",
      "custom_content_update",
      "review_open",
      "review_approve",
      "review_close",
      "reviewer_add",
      "reviewer_remove",
      "set_custom_url",
    ]),
    user_id: z.string().nullable(),
    user: UserResponseDto.nullable(),
    referenced_user: UserResponseDto.nullable(),
  })
  .strict();
export type PageAuditLogsResponseDto = z.infer<typeof PageAuditLogsResponseDto>;

export const PageAuditLogsDataResponseDto = z
  .object({ data: z.array(PageAuditLogsResponseDto) })
  .strict();
export type PageAuditLogsDataResponseDto = z.infer<
  typeof PageAuditLogsDataResponseDto
>;

export const ArchivedPageDataResponseDto = z
  .object({ data: PageFullResponseDto })
  .strict();
export type ArchivedPageDataResponseDto = z.infer<
  typeof ArchivedPageDataResponseDto
>;

export const PublishedPageDataResponseDto = z
  .object({ data: PageFullResponseDto })
  .strict();
export type PublishedPageDataResponseDto = z.infer<
  typeof PublishedPageDataResponseDto
>;

export const Object = z.object({}).partial().strict();
export type Object = z.infer<typeof Object>;

export const ComponentType = z
  .object({
    name: z.string().min(1),
    props_schema: z.object({}).partial().strict().passthrough(),
    preview_image_sanity_reference_id: z.string(),
    tags: z.array(z.string()).optional(),
    id: z.number(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();
export type ComponentType = z.infer<typeof ComponentType>;

export const MetaDto = z
  .object({
    total_count: z.number(),
    page_number: z.number().default(1),
    page_size: z.number().default(10),
  })
  .partial()
  .strict();
export type MetaDto = z.infer<typeof MetaDto>;

export const ComponentTypesResponseDto = z
  .object({ data: z.array(ComponentType), meta: MetaDto.optional() })
  .strict();
export type ComponentTypesResponseDto = z.infer<
  typeof ComponentTypesResponseDto
>;

export const ErrorResponseDto = z
  .object({
    message: z.union([z.string(), z.array(z.string())]),
    error: z.enum([
      "BAD_REQUEST",
      "UNAUTHORIZED",
      "FORBIDDEN",
      "NOT_FOUND",
      "CONFLICT",
      "INTERNAL_SERVER_ERROR",
    ]),
    status_code: z.number(),
  })
  .strict();
export type ErrorResponseDto = z.infer<typeof ErrorResponseDto>;

export const ComponentTypeRequestDto = z
  .object({
    name: z.string().min(1),
    props_schema: z.object({}).partial().strict().passthrough(),
    preview_image_sanity_reference_id: z.string(),
    tags: z.array(z.string()).optional(),
  })
  .strict();
export type ComponentTypeRequestDto = z.infer<typeof ComponentTypeRequestDto>;

export const ComponentTypeResponseDto = z
  .object({ data: ComponentType })
  .strict();
export type ComponentTypeResponseDto = z.infer<typeof ComponentTypeResponseDto>;

export const PartialComponentTypeRequestDto = z
  .object({
    name: z.string().min(1),
    props_schema: z.object({}).partial().strict().passthrough(),
    preview_image_sanity_reference_id: z.string(),
    tags: z.array(z.string()),
  })
  .partial()
  .strict();
export type PartialComponentTypeRequestDto = z.infer<
  typeof PartialComponentTypeRequestDto
>;

export const ComponentTypeNameUniquenessRequestDto = z
  .object({ name: z.string().min(1), exclude_id: z.number().optional() })
  .strict();
export type ComponentTypeNameUniquenessRequestDto = z.infer<
  typeof ComponentTypeNameUniquenessRequestDto
>;

export const ComponentTypeNameUniquenessDto = z
  .object({ is_valid: z.boolean() })
  .strict();
export type ComponentTypeNameUniquenessDto = z.infer<
  typeof ComponentTypeNameUniquenessDto
>;

export const ComponentTypeNameUniquenessResponseDto = z
  .object({ data: ComponentTypeNameUniquenessDto })
  .strict();
export type ComponentTypeNameUniquenessResponseDto = z.infer<
  typeof ComponentTypeNameUniquenessResponseDto
>;

export const CreatedByUser = z
  .object({
    avatar_url: z.string(),
    display_name: z.string(),
    email: z.string().email(),
    type: z.enum(["admin", "user"]),
  })
  .strict();
export type CreatedByUser = z.infer<typeof CreatedByUser>;

export const ComponentVariantWithUserDto = z
  .object({
    name: z.string().min(1),
    component_type_id: z.number(),
    parent_component_variant: z.number().nullish(),
    is_archived: z.boolean().optional().default(false),
    is_base: z.boolean().optional().default(false),
    tags: z.array(z.string()).optional(),
    id: z.number(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    created_by_user: CreatedByUser,
    component_type_name: z.string(),
  })
  .strict();
export type ComponentVariantWithUserDto = z.infer<
  typeof ComponentVariantWithUserDto
>;

export const ComponentVariantsResponseDto = z
  .object({
    data: z.array(ComponentVariantWithUserDto),
    meta: MetaDto.optional(),
  })
  .strict();
export type ComponentVariantsResponseDto = z.infer<
  typeof ComponentVariantsResponseDto
>;

export const ComponentVariantRequestDto = z
  .object({
    name: z.string().min(1),
    component_type_id: z.number(),
    parent_component_variant: z.number().nullish(),
    is_archived: z.boolean().optional().default(false),
    is_base: z.boolean().optional().default(false),
    tags: z.array(z.string()).optional(),
    props: z.object({}).partial().strict().passthrough(),
    locale: z
      .enum([
        "us",
        "gb",
        "de",
        "nl",
        "se",
        "eu",
        "es",
        "fr",
        "jp",
        "apac-us",
        "au",
        "nz",
      ])
      .optional(),
  })
  .strict();
export type ComponentVariantRequestDto = z.infer<
  typeof ComponentVariantRequestDto
>;

export const ComponentVariantVersionWithUserDto = z
  .object({
    version: z.number().gte(1),
    component_variant_id: z.number(),
    props: z.object({}).partial().strict().passthrough(),
    is_active: z.boolean().optional().default(true),
    title: z.string().nullish(),
    description: z.string().nullish(),
    locale: z
      .enum([
        "us",
        "gb",
        "de",
        "nl",
        "se",
        "eu",
        "es",
        "fr",
        "jp",
        "apac-us",
        "au",
        "nz",
      ])
      .optional()
      .default("us"),
    action: z.enum(["create", "edit", "restore"]).optional().default("create"),
    status: z
      .enum(["draft", "published", "review"])
      .optional()
      .default("draft"),
    id: z.number(),
    created_at: z.string().datetime({ offset: true }),
    created_by_user: CreatedByUser,
  })
  .strict();
export type ComponentVariantVersionWithUserDto = z.infer<
  typeof ComponentVariantVersionWithUserDto
>;

export const ComponentVariantWithUserAndVersionDto = z
  .object({
    name: z.string().min(1),
    component_type_id: z.number(),
    parent_component_variant: z.number().nullish(),
    is_archived: z.boolean().optional().default(false),
    is_base: z.boolean().optional().default(false),
    tags: z.array(z.string()).optional(),
    id: z.number(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    created_by_user: CreatedByUser,
    component_type_name: z.string(),
    version: ComponentVariantVersionWithUserDto.optional(),
  })
  .strict();
export type ComponentVariantWithUserAndVersionDto = z.infer<
  typeof ComponentVariantWithUserAndVersionDto
>;

export const ComponentVariantWithUserAndVersionResponseDto = z
  .object({ data: ComponentVariantWithUserAndVersionDto })
  .strict();
export type ComponentVariantWithUserAndVersionResponseDto = z.infer<
  typeof ComponentVariantWithUserAndVersionResponseDto
>;

export const ComponentVariantWithUserResponseDto = z
  .object({ data: ComponentVariantWithUserDto })
  .strict();
export type ComponentVariantWithUserResponseDto = z.infer<
  typeof ComponentVariantWithUserResponseDto
>;

export const PartialComponentVariantRequestDto = z
  .object({
    name: z.string().min(1),
    component_type_id: z.number(),
    parent_component_variant: z.number(),
    is_archived: z.boolean().default(false),
    is_base: z.boolean().default(false),
    tags: z.array(z.string()),
    props: z.object({}).partial().strict().passthrough(),
    locale: z.enum([
      "us",
      "gb",
      "de",
      "nl",
      "se",
      "eu",
      "es",
      "fr",
      "jp",
      "apac-us",
      "au",
      "nz",
    ]),
  })
  .partial()
  .strict();
export type PartialComponentVariantRequestDto = z.infer<
  typeof PartialComponentVariantRequestDto
>;

export const ComponentVariantNameUniquenessRequestDto = z
  .object({ name: z.string().min(1), exclude_id: z.number().optional() })
  .strict();
export type ComponentVariantNameUniquenessRequestDto = z.infer<
  typeof ComponentVariantNameUniquenessRequestDto
>;

export const ComponentVariantNameUniquenessDto = z
  .object({ is_valid: z.boolean() })
  .strict();
export type ComponentVariantNameUniquenessDto = z.infer<
  typeof ComponentVariantNameUniquenessDto
>;

export const ComponentVariantNameUniquenessResponseDto = z
  .object({ data: ComponentVariantNameUniquenessDto })
  .strict();
export type ComponentVariantNameUniquenessResponseDto = z.infer<
  typeof ComponentVariantNameUniquenessResponseDto
>;

export const ComponentVariantVersionsResponseDto = z
  .object({
    data: z.array(ComponentVariantVersionWithUserDto),
    meta: MetaDto.optional(),
  })
  .strict();
export type ComponentVariantVersionsResponseDto = z.infer<
  typeof ComponentVariantVersionsResponseDto
>;

export const ComponentVariantVersionWithUserResponseDto = z
  .object({ data: ComponentVariantVersionWithUserDto.nullable() })
  .strict();
export type ComponentVariantVersionWithUserResponseDto = z.infer<
  typeof ComponentVariantVersionWithUserResponseDto
>;

export const PartialUpdateComponentVariantVersionRequestDto = z
  .object({ title: z.string(), description: z.string() })
  .partial()
  .strict();
export type PartialUpdateComponentVariantVersionRequestDto = z.infer<
  typeof PartialUpdateComponentVariantVersionRequestDto
>;

export const PublishComponentVariantVersionRequestDto = z
  .object({ props: z.object({}).partial().strict().passthrough() })
  .partial()
  .strict();
export type PublishComponentVariantVersionRequestDto = z.infer<
  typeof PublishComponentVariantVersionRequestDto
>;

export const ReviewDto = z
  .object({
    id: z.number(),
    customerName: z.string(),
    title: z.string(),
    rating: z.number().gte(1).lte(5),
    content: z.string(),
    reviewDate: z.number(),
    productLabel: z.enum(["AG1", "AG1 Next Gen", "AGZ"]),
    verifiedPurchase: z.boolean(),
    helpfulCount: z.number(),
    sku: z.string().optional(),
  })
  .strict();
export type ReviewDto = z.infer<typeof ReviewDto>;

export const ReviewsResponseDto = z
  .object({
    reviews: z.array(ReviewDto),
    totalCount: z.number(),
    ratingCounts: z.array(
      z.object({ rating: z.number(), count: z.number() }).strict()
    ),
    page: z.number(),
    pageSize: z.number(),
    hasMore: z.boolean(),
  })
  .strict();
export type ReviewsResponseDto = z.infer<typeof ReviewsResponseDto>;

export const CreateReviewDto = z
  .object({
    sku: z.string().optional(),
    product_title: z.string().optional(),
    product_description: z.string().optional(),
    product_url: z.string().optional(),
    product_image_url: z.string().optional(),
    display_name: z.string(),
    email: z.string().email(),
    is_incentivized: z.boolean().optional(),
    review_content: z.string(),
    review_title: z.string(),
    review_score: z.number().gte(1).lte(5),
    review_source: z.string().optional(),
    uploaded_image: z
      .array(z.object({}).partial().strict().passthrough())
      .optional(),
  })
  .strict();
export type CreateReviewDto = z.infer<typeof CreateReviewDto>;

export const CreateReviewResponseDto = z
  .object({ success: z.boolean(), message: z.string(), review: ReviewDto })
  .strict();
export type CreateReviewResponseDto = z.infer<typeof CreateReviewResponseDto>;

export const GenerateTextResponseDto = z
  .object({ result: z.string() })
  .strict();
export type GenerateTextResponseDto = z.infer<typeof GenerateTextResponseDto>;

export const CoreVitalMetricDto = z
  .object({
    value: z.string(),
    status: z.enum(["good", "needs-improvement", "poor"]),
  })
  .strict();
export type CoreVitalMetricDto = z.infer<typeof CoreVitalMetricDto>;

export const CoreVitalPercentilesDto = z
  .object({
    p75: CoreVitalMetricDto,
    p90: CoreVitalMetricDto,
    p95: CoreVitalMetricDto,
  })
  .strict();
export type CoreVitalPercentilesDto = z.infer<typeof CoreVitalPercentilesDto>;

export const FormattedCoreVitalsDataDto = z
  .object({
    LCP: CoreVitalPercentilesDto,
    CLS: CoreVitalPercentilesDto,
    INP: CoreVitalPercentilesDto,
  })
  .strict();
export type FormattedCoreVitalsDataDto = z.infer<
  typeof FormattedCoreVitalsDataDto
>;

export const UpdateUserTypeDto = z
  .object({ type: z.enum(["admin", "user"]) })
  .strict();
export type UpdateUserTypeDto = z.infer<typeof UpdateUserTypeDto>;

export const AnalyticsByPageIdsDto = z
  .object({ pageIds: z.array(z.number()) })
  .strict();
export type AnalyticsByPageIdsDto = z.infer<typeof AnalyticsByPageIdsDto>;

export const AnalyticsPageCacheDataDto = z
  .object({
    date: z.string(),
    numSessions: z.number(),
    avgDurationMin: z.number(),
    numBounces: z.number(),
    numCvrs: z.number(),
  })
  .strict();
export type AnalyticsPageCacheDataDto = z.infer<
  typeof AnalyticsPageCacheDataDto
>;

export const AnalyticsPageCacheDto = z
  .object({
    data: z.array(AnalyticsPageCacheDataDto),
    id: z.number(),
    page_id: z.number(),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();
export type AnalyticsPageCacheDto = z.infer<typeof AnalyticsPageCacheDto>;

export const AnalyticsByPageIdsResponseDto = z
  .object({ data: z.array(AnalyticsPageCacheDto) })
  .strict();
export type AnalyticsByPageIdsResponseDto = z.infer<
  typeof AnalyticsByPageIdsResponseDto
>;

export const CachePageMetricsResponseDto = z
  .object({
    numChunks: z.number(),
    numPagesUpdated: z.number(),
    lastError: z.string(),
  })
  .partial()
  .strict();
export type CachePageMetricsResponseDto = z.infer<
  typeof CachePageMetricsResponseDto
>;

export const SitemapResponseDto = z
  .object({
    from: z.string(),
    id: z.number(),
    is_no_index_page: z.boolean().nullable(),
  })
  .strict();
export type SitemapResponseDto = z.infer<typeof SitemapResponseDto>;

export const SitemapDataResponseDto = z
  .object({ data: z.array(SitemapResponseDto) })
  .strict();
export type SitemapDataResponseDto = z.infer<typeof SitemapDataResponseDto>;

export const NotificationsHasNewResponseDto = z
  .object({ data: z.number() })
  .strict();
export type NotificationsHasNewResponseDto = z.infer<
  typeof NotificationsHasNewResponseDto
>;

export const NotificationListItemDto = z
  .object({
    id: z.number(),
    page_name: z.string(),
    page_id: z.number().optional(),
    title: z.string(),
    location: z.string().optional(),
    created_at: z.string().datetime({ offset: true }),
    created_by_name: z.string().optional(),
    requires_action: z.boolean(),
    avatar_url: z.string().optional(),
    event_type: z.string(),
  })
  .strict();
export type NotificationListItemDto = z.infer<typeof NotificationListItemDto>;

export const NotificationsListResponseDto = z
  .object({
    hasMore: z.boolean(),
    notifications: z.array(NotificationListItemDto),
    totalActionNeededCount: z.number(),
    watermarkDate: z.string().optional(),
    newWatermark: z.string().optional(),
  })
  .strict();
export type NotificationsListResponseDto = z.infer<
  typeof NotificationsListResponseDto
>;

export const FullNotificationsListResponseDto = z
  .object({ data: NotificationsListResponseDto })
  .strict();
export type FullNotificationsListResponseDto = z.infer<
  typeof FullNotificationsListResponseDto
>;

export const CreateRoleDto = z
  .object({
    userId: z.string(),
    email: z.string(),
    role: z.string(),
    pageId: z.number(),
  })
  .partial()
  .strict();
export type CreateRoleDto = z.infer<typeof CreateRoleDto>;

export const CreateRoleResponseDataDto = z
  .object({ status: z.string() })
  .strict();
export type CreateRoleResponseDataDto = z.infer<
  typeof CreateRoleResponseDataDto
>;

export const CreateRoleResponseDto = z
  .object({ data: CreateRoleResponseDataDto })
  .strict();
export type CreateRoleResponseDto = z.infer<typeof CreateRoleResponseDto>;

export const RemoveRoleDto = z
  .object({ userId: z.string(), pageId: z.number() })
  .partial()
  .strict();
export type RemoveRoleDto = z.infer<typeof RemoveRoleDto>;

export const RemoveRoleResponseDataDto = z
  .object({ status: z.string() })
  .strict();
export type RemoveRoleResponseDataDto = z.infer<
  typeof RemoveRoleResponseDataDto
>;

export const RemoveRoleResponseDto = z
  .object({ data: RemoveRoleResponseDataDto })
  .strict();
export type RemoveRoleResponseDto = z.infer<typeof RemoveRoleResponseDto>;

export const PageReviewIsOpenDataResponseDto = z
  .object({ data: z.record(z.boolean()) })
  .strict();
export type PageReviewIsOpenDataResponseDto = z.infer<
  typeof PageReviewIsOpenDataResponseDto
>;

export const PageReviewDto = z
  .object({
    id: z.number(),
    is_active: z.boolean(),
    created_at: z.string().datetime({ offset: true }),
    last_approval_at: z.string().datetime({ offset: true }).nullable(),
    slack_message_thread_id: z.string().nullable(),
    page_id: z.number(),
  })
  .strict();
export type PageReviewDto = z.infer<typeof PageReviewDto>;

export const UserDto = z
  .object({
    id: z.string(),
    email: z.string().nullable(),
    display_name: z.string().nullable(),
    avatar_url: z.string().nullable(),
    type: z.string(),
    created_at: z.string().datetime({ offset: true }),
  })
  .strict();
export type UserDto = z.infer<typeof UserDto>;

export const PageReviewUserDto = z
  .object({
    id: z.number(),
    review_id: z.number(),
    user_id: z.string(),
    has_approved: z.boolean(),
    created_at: z.string().datetime({ offset: true }),
    user: UserDto.nullable(),
  })
  .strict();
export type PageReviewUserDto = z.infer<typeof PageReviewUserDto>;

export const PageReviewResponseDto = z
  .object({ review: PageReviewDto, reviewUsers: z.array(PageReviewUserDto) })
  .strict();
export type PageReviewResponseDto = z.infer<typeof PageReviewResponseDto>;

export const PageReviewDataResponseDto = z
  .object({ data: PageReviewResponseDto })
  .strict();
export type PageReviewDataResponseDto = z.infer<
  typeof PageReviewDataResponseDto
>;

export const PageReviewUserResponseDto = z
  .object({ reviewUser: PageReviewUserDto })
  .strict();
export type PageReviewUserResponseDto = z.infer<
  typeof PageReviewUserResponseDto
>;

export const PageReviewUserDataResponseDto = z
  .object({ data: PageReviewUserResponseDto })
  .strict();
export type PageReviewUserDataResponseDto = z.infer<
  typeof PageReviewUserDataResponseDto
>;

export const PageReviewCloseResponseDto = z
  .object({ review: PageReviewDto })
  .strict();
export type PageReviewCloseResponseDto = z.infer<
  typeof PageReviewCloseResponseDto
>;

export const PageReviewCloseUserDataResponseDto = z
  .object({ data: PageReviewCloseResponseDto })
  .strict();
export type PageReviewCloseUserDataResponseDto = z.infer<
  typeof PageReviewCloseUserDataResponseDto
>;

export const CreatePageReviewRequestDto = z
  .object({
    pageId: z.number().gte(1),
    reviewerUserIds: z.array(z.string()).min(1),
    threadId: z.string(),
  })
  .strict();
export type CreatePageReviewRequestDto = z.infer<
  typeof CreatePageReviewRequestDto
>;

export const CreateReviewUserRequestDto = z
  .object({ reviewId: z.number().gte(1), userId: z.array(z.string()).min(1) })
  .strict();
export type CreateReviewUserRequestDto = z.infer<
  typeof CreateReviewUserRequestDto
>;

export const CreateReviewUserDataResponseDto = z
  .object({ data: PageReviewUserResponseDto })
  .strict();
export type CreateReviewUserDataResponseDto = z.infer<
  typeof CreateReviewUserDataResponseDto
>;

export const ReviewDefaultUsersResponseDto = z
  .object({ users: z.array(PageReviewUserDto) })
  .strict();
export type ReviewDefaultUsersResponseDto = z.infer<
  typeof ReviewDefaultUsersResponseDto
>;

export const ReviewDefaultUsersDataResponseDto = z
  .object({ data: ReviewDefaultUsersResponseDto })
  .strict();
export type ReviewDefaultUsersDataResponseDto = z.infer<
  typeof ReviewDefaultUsersDataResponseDto
>;

export const RemoveReviewUserRequestDto = z
  .object({ reviewId: z.number().gte(1), userId: z.array(z.string()).min(1) })
  .strict();
export type RemoveReviewUserRequestDto = z.infer<
  typeof RemoveReviewUserRequestDto
>;

export const RemoveReviewUserResponseDto = z
  .object({ data: z.object({}).partial().strict().passthrough() })
  .strict();
export type RemoveReviewUserResponseDto = z.infer<
  typeof RemoveReviewUserResponseDto
>;

export const DiscountCodesResponseDto = z
  .object({ discountCodes: z.array(z.string()) })
  .strict();
export type DiscountCodesResponseDto = z.infer<typeof DiscountCodesResponseDto>;

const endpoints = makeApi([
  {
    method: "get",
    path: "/",
    alias: "getHello",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/ai/generate-text",
    alias: "generate",
    requestFormat: "json",
    parameters: [
      {
        name: "prompt",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.object({ result: z.string() }).strict(),
  },
  {
    method: "get",
    path: "/analytics/cron/cache-page-metrics",
    alias: "cachePageMetrics",
    requestFormat: "json",
    response: CachePageMetricsResponseDto,
  },
  {
    method: "post",
    path: "/analytics/fetch",
    alias: "byPageIds",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AnalyticsByPageIdsDto,
      },
    ],
    response: AnalyticsByPageIdsResponseDto,
    errors: [
      {
        status: 500,
        description: `Error fetching analytics data`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-types",
    alias: "getComponentTypes",
    requestFormat: "json",
    parameters: [
      {
        name: "page_number",
        type: "Query",
        schema: z.number().gte(1).optional().default(1),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z.number().gte(1).optional().default(10),
      },
      {
        name: "tags",
        type: "Query",
        schema: z.array(z.any()).optional(),
      },
    ],
    response: ComponentTypesResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "post",
    path: "/asset-library/component-types",
    alias: "createComponentType",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ComponentTypeRequestDto,
      },
    ],
    response: ComponentTypeResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 409,
        description: `Component type already exists`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-types/:componentTypeId",
    alias: "getComponentTypeById",
    requestFormat: "json",
    parameters: [
      {
        name: "componentTypeId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentTypeResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component types found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "put",
    path: "/asset-library/component-types/:componentTypeId",
    alias: "updateComponentTypeById",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ComponentTypeRequestDto,
      },
      {
        name: "componentTypeId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentTypeResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component type was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 409,
        description: `Component type already exists`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "patch",
    path: "/asset-library/component-types/:componentTypeId",
    alias: "partiallyUpdateComponentType",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartialComponentTypeRequestDto,
      },
      {
        name: "componentTypeId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentTypeResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component type was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 409,
        description: `Component type already exists`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "delete",
    path: "/asset-library/component-types/:componentTypeId",
    alias: "deleteComponentType",
    requestFormat: "json",
    parameters: [
      {
        name: "componentTypeId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component type was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "post",
    path: "/asset-library/component-types/validate-name-uniqueness",
    alias: "validateComponentTypeNameUniqueness",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ComponentTypeNameUniquenessRequestDto,
      },
    ],
    response: ComponentTypeNameUniquenessResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-variants",
    alias: "getComponentVariants",
    requestFormat: "json",
    parameters: [
      {
        name: "page_number",
        type: "Query",
        schema: z.number().gte(1).optional().default(1),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z.number().gte(1).optional().default(10),
      },
      {
        name: "component_type_id",
        type: "Query",
        schema: z.number().gte(0).optional(),
      },
      {
        name: "base",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "archived",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "tags",
        type: "Query",
        schema: z.array(z.any()).optional(),
      },
    ],
    response: ComponentVariantsResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "post",
    path: "/asset-library/component-variants",
    alias: "createComponentVariant",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ComponentVariantRequestDto,
      },
    ],
    response: ComponentVariantWithUserAndVersionResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 409,
        description: `Component variant already exists`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-variants/:componentVariantId",
    alias: "getComponentVariantById",
    requestFormat: "json",
    parameters: [
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentVariantWithUserResponseDto,
    errors: [
      {
        status: 404,
        description: `No component variants found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "put",
    path: "/asset-library/component-variants/:componentVariantId",
    alias: "updateComponentVariantById",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ComponentVariantRequestDto,
      },
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentVariantWithUserAndVersionResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 409,
        description: `Component variant already exists`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "patch",
    path: "/asset-library/component-variants/:componentVariantId",
    alias: "partiallyUpdateComponentVariant",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartialComponentVariantRequestDto,
      },
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentVariantWithUserAndVersionResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 409,
        description: `Component variant already exists`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "delete",
    path: "/asset-library/component-variants/:componentVariantId",
    alias: "deleteComponentVariant",
    requestFormat: "json",
    parameters: [
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-variants/:componentVariantId/versions",
    alias: "getComponentVariantVersions",
    requestFormat: "json",
    parameters: [
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "locale",
        type: "Query",
        schema: z
          .enum([
            "us",
            "gb",
            "de",
            "nl",
            "se",
            "eu",
            "es",
            "fr",
            "jp",
            "apac-us",
            "au",
            "nz",
          ])
          .optional(),
      },
      {
        name: "page_number",
        type: "Query",
        schema: z.number().gte(1).optional().default(1),
      },
      {
        name: "page_size",
        type: "Query",
        schema: z.number().gte(1).optional().default(10),
      },
    ],
    response: ComponentVariantVersionsResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-variants/:componentVariantId/versions/:version",
    alias: "getComponentVariantVersionById",
    requestFormat: "json",
    parameters: [
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "version",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentVariantVersionWithUserResponseDto,
    errors: [
      {
        status: 404,
        description: `No component variants found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "patch",
    path: "/asset-library/component-variants/:componentVariantId/versions/:version",
    alias: "partiallyUpdateComponentVariantVersion",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PartialUpdateComponentVariantVersionRequestDto,
      },
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "version",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: PartialUpdateComponentVariantVersionRequestDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "post",
    path: "/asset-library/component-variants/:componentVariantId/versions/:version/publish",
    alias: "publishVersion",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ props: z.object({}).partial().strict().passthrough() })
          .partial()
          .strict(),
      },
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "version",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentVariantVersionWithUserResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "post",
    path: "/asset-library/component-variants/:componentVariantId/versions/:version/restore",
    alias: "restoreToVersion",
    requestFormat: "json",
    parameters: [
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "version",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ComponentVariantVersionWithUserResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 404,
        description: `No component variant was found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/asset-library/component-variants/:componentVariantId/versions/active",
    alias: "getActiveComponentVariantVersion",
    requestFormat: "json",
    parameters: [
      {
        name: "componentVariantId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "locale",
        type: "Query",
        schema: z
          .enum([
            "us",
            "gb",
            "de",
            "nl",
            "se",
            "eu",
            "es",
            "fr",
            "jp",
            "apac-us",
            "au",
            "nz",
          ])
          .optional(),
      },
    ],
    response: ComponentVariantVersionWithUserResponseDto,
    errors: [
      {
        status: 404,
        description: `No component variant found`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "post",
    path: "/asset-library/component-variants/validate-name-uniqueness",
    alias: "validateComponentVariantNameUniqueness",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ComponentVariantNameUniquenessRequestDto,
      },
    ],
    response: ComponentVariantNameUniquenessResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid request parameters`,
        schema: ErrorResponseDto,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponseDto,
      },
    ],
  },
  {
    method: "get",
    path: "/core-web-vitals",
    alias: "getVitals",
    requestFormat: "json",
    parameters: [
      {
        name: "deviceType",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/core-web-vitals/page",
    alias: "getVitalsByPage",
    requestFormat: "json",
    parameters: [
      {
        name: "path",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: FormattedCoreVitalsDataDto,
  },
  {
    method: "post",
    path: "/experiments",
    alias: "createExperiment",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Experiment creation payload`,
        type: "Body",
        schema: z.object({}).partial().strict(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/experiments",
    alias: "getAllExperiments",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/experiments/:id",
    alias: "getExperiment",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Experiment not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/experiments/:id/abandon",
    alias: "abandonExperiment",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/decide",
    alias: "decide",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.unknown(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/delete",
    alias: "deleteDraft",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/pause",
    alias: "pause",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/restart",
    alias: "restart",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/resume",
    alias: "resume",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/start",
    alias: "start",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/experiments/:id/variants",
    alias: "getVariants",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/experiments/:id/variants/create",
    alias: "createVariant",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/experiments/control-page-options",
    alias: "getControlPageOptions",
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "experimentId",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/experiments/is-control-page/:pageId",
    alias: "isControlPage",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/experiments/variants/:variantId",
    alias: "deleteVariant",
    requestFormat: "json",
    parameters: [
      {
        name: "variantId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/frictionless/discount-codes/:id",
    alias: "getDiscountCodes",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: DiscountCodesResponseDto,
  },
  {
    method: "get",
    path: "/health-check",
    alias: "getHealth",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/notifications/has-new",
    alias: "hasNew",
    requestFormat: "json",
    parameters: [
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: z.object({ data: z.number() }).strict(),
  },
  {
    method: "get",
    path: "/notifications/list",
    alias: "list",
    requestFormat: "json",
    parameters: [
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: FullNotificationsListResponseDto,
    errors: [
      {
        status: 400,
        description: `limit is not a number/limit is out of range`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/offers/:offerId/:locale",
    alias: "getOffer",
    requestFormat: "json",
    parameters: [
      {
        name: "offerId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "locale",
        type: "Path",
        schema: z.enum([
          "en-us",
          "apac-us",
          "en-au",
          "en-nz",
          "en-uk",
          "en-eu",
          "de-eu",
          "se-eu",
          "nl-eu",
          "es-eu",
          "fr-eu",
          "en-jp",
          "ja-jp",
          "en-dev",
          "en-qa",
          "en-aud",
          "en-nzd",
          "de-qa",
          "en-eud",
          "de-eud",
          "en-ukd",
        ]),
      },
    ],
    response: OfferDataResponseDto,
  },
  {
    method: "get",
    path: "/offers/context-props/:offerId/:locale",
    alias: "offerDataForPage",
    requestFormat: "json",
    parameters: [
      {
        name: "offerId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "locale",
        type: "Path",
        schema: z.enum([
          "en-us",
          "apac-us",
          "en-au",
          "en-nz",
          "en-uk",
          "en-eu",
          "de-eu",
          "se-eu",
          "nl-eu",
          "es-eu",
          "fr-eu",
          "en-jp",
          "ja-jp",
          "en-dev",
          "en-qa",
          "en-aud",
          "en-nzd",
          "de-qa",
          "en-eud",
          "de-eud",
          "en-ukd",
        ]),
      },
    ],
    response: OfferDataForPageResponseDto,
  },
  {
    method: "get",
    path: "/offers/v2/:offerId",
    alias: "getOfferV2",
    requestFormat: "json",
    parameters: [
      {
        name: "offerId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OfferV2ResponseDto,
  },
  {
    method: "get",
    path: "/offers/v2/context-props/:offerId/:locale",
    alias: "offerDataForPageV2",
    requestFormat: "json",
    parameters: [
      {
        name: "offerId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ data: z.object({}).partial().strict() }).strict(),
  },
  {
    method: "post",
    path: "/page",
    alias: "createPage",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PageCreateRequestDto,
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: PageCreateResponseDataDto,
    errors: [
      {
        status: 400,
        description: `Page name already taken`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/:pageId",
    alias: "getPageById",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "locale",
        type: "Query",
        schema: z
          .enum([
            "us",
            "gb",
            "de",
            "nl",
            "se",
            "eu",
            "es",
            "fr",
            "jp",
            "apac-us",
            "au",
            "nz",
          ])
          .optional(),
      },
    ],
    response: PageResponseDto,
  },
  {
    method: "put",
    path: "/page/:pageId/archive",
    alias: "archivePage",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: ArchivedPageDataResponseDto,
    errors: [
      {
        status: 400,
        description: `page is already in target status`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to modify page.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Page not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `staged draft page`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/:pageId/audit-logs",
    alias: "fetchAuditLogs",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.number(),
      },
    ],
    response: PageAuditLogsDataResponseDto,
  },
  {
    method: "get",
    path: "/page/:pageId/custom-content",
    alias: "fetchCustomContent",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "regions",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: CustomContentResponseDto,
  },
  {
    method: "post",
    path: "/page/:pageId/custom-url",
    alias: "createCustomUrl",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ url: z.string() }).strict(),
      },
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: CreateCustomUrlDataResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid custom URL format or staged draft page`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to modify the page`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Page not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/page/:pageId/duplicate",
    alias: "duplicatePage",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: PageCreateResponseDataDto,
    errors: [
      {
        status: 403,
        description: `User does not have permissions to duplicate the page`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Page not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/:pageId/experiment",
    alias: "getPageExperiment",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: PageExperimentResponseDataDto,
    errors: [
      {
        status: 404,
        description: `Page not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "put",
    path: "/page/:pageId/publish",
    alias: "publishPage",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: PublishedPageDataResponseDto,
    errors: [
      {
        status: 400,
        description: `Page is not archived`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to modify page.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Page not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `staged draft page`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/:pageId/roles",
    alias: "getPageRoles",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: PageRolesDataResponseDto,
    errors: [
      {
        status: 403,
        description: `User does not have permissions to modify page.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/:pageId/seo-tags",
    alias: "fetchSeoTags",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: PageSeoTagsDataResponseDto,
  },
  {
    method: "get",
    path: "/page/name-check",
    alias: "getCheckPageName",
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "Authorization",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: PageNameCheckDataResponseDto,
    errors: [
      {
        status: 403,
        description: `User does not have permissions to modify page.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/page/reviews",
    alias: "createPageReview",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePageReviewRequestDto,
      },
    ],
    response: PageReviewDataResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid page ID, invalid user IDs, or page is not in a valid state to create a review`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have access to page`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/reviews/:pageId",
    alias: "getPageReviews",
    requestFormat: "json",
    parameters: [
      {
        name: "pageId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: PageReviewDataResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid page ID`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to view review for page`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "put",
    path: "/page/reviews/:reviewId/approve",
    alias: "approvePageReview",
    requestFormat: "json",
    parameters: [
      {
        name: "reviewId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: PageReviewUserDataResponseDto,
    errors: [
      {
        status: 400,
        description: `Review is not active or invalid review ID`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to modify review`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Review not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "put",
    path: "/page/reviews/:reviewId/close",
    alias: "closePageReview",
    requestFormat: "json",
    parameters: [
      {
        name: "reviewId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: PageReviewCloseUserDataResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have access to review`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Review not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/reviews/is-open",
    alias: "getPagesHaveOpenReviews",
    requestFormat: "json",
    parameters: [
      {
        name: "pageIds",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: PageReviewIsOpenDataResponseDto,
    errors: [
      {
        status: 400,
        description: `No page IDs provided or invalid page IDs`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/page/reviews/user",
    alias: "createReviewUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateReviewUserRequestDto,
      },
    ],
    response: CreateReviewUserDataResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid review ID or user ID`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to add users to review`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Review not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/reviews/user/default",
    alias: "getDefaultReviewUsers",
    requestFormat: "json",
    response: ReviewDefaultUsersDataResponseDto,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "delete",
    path: "/page/reviews/user/remove",
    alias: "removeReviewUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RemoveReviewUserRequestDto,
      },
    ],
    response: z
      .object({ data: z.object({}).partial().strict().passthrough() })
      .strict(),
    errors: [
      {
        status: 400,
        description: `Invalid review ID or user ID`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `User does not have permissions to remove users from review`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Review or review user not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/page/search",
    alias: "searchPages",
    requestFormat: "json",
    parameters: [
      {
        name: "query",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: PageSearchResponseDataDto,
  },
  {
    method: "get",
    path: "/reviews",
    alias: "getReviews",
    requestFormat: "json",
    parameters: [
      {
        name: "formulaVersion",
        type: "Query",
        schema: z.enum(["V52", "V53", "AGZ"]).optional(),
      },
      {
        name: "page",
        type: "Query",
        schema: z.number().gte(1).optional().default(1),
      },
      {
        name: "pageSize",
        type: "Query",
        schema: z.number().gte(1).lte(100).optional().default(20),
      },
      {
        name: "sortBy",
        type: "Query",
        schema: z.enum(["date", "rating", "helpful"]).optional(),
      },
      {
        name: "sortOrder",
        type: "Query",
        schema: z.enum(["asc", "desc"]).optional(),
      },
      {
        name: "sku",
        type: "Query",
        schema: z
          .enum([
            "FG-20003",
            "FG-20038",
            "FG-20059",
            "FG-20058",
            "FG-20043",
            "FG-20042",
            "FG-20122",
          ])
          .optional(),
      },
      {
        name: "rating",
        type: "Query",
        schema: z.number().gte(1).lte(5).optional(),
      },
      {
        name: "keyword",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: ReviewsResponseDto,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/reviews",
    alias: "createReview",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateReviewDto,
      },
    ],
    response: CreateReviewResponseDto,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/reviews/:id",
    alias: "getReviewById",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ReviewDto,
    errors: [
      {
        status: 404,
        description: `Review not found`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/role/create",
    alias: "createRole",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateRoleDto,
      },
    ],
    response: CreateRoleResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input: role must be a valid string (Viewer/Editor), pageId must be a positive number, userId must be a string, or email must be valid`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden: User does not have access to modify roles for the page`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `No user found for the provided email`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error: Failed to create/update role or log audit action`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/role/remove",
    alias: "removeRole",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RemoveRoleDto,
      },
    ],
    response: RemoveRoleResponseDto,
    errors: [
      {
        status: 400,
        description: `Invalid input: userId must be a string, pageId must be a positive number`,
        schema: z.void(),
      },
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 403,
        description: `Forbidden: User does not have access to the page`,
        schema: z.void(),
      },
      {
        status: 500,
        description: `Internal server error: Failed to delete role or log audit action`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/sitemap",
    alias: "getSitemapData",
    requestFormat: "json",
    response: SitemapDataResponseDto,
  },
  {
    method: "get",
    path: "/users/is-admin/:id",
    alias: "checkIsAdmin",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/users/list",
    alias: "listUsers",
    requestFormat: "json",
    parameters: [
      {
        name: "currentPage",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "paginationSortType",
        type: "Query",
        schema: z.unknown().optional(),
      },
      {
        name: "paginationLimit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "rangeTo",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "rangeFrom",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "nameFilter",
        type: "Query",
        schema: z.unknown().optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.unknown().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/users/search",
    alias: "searchUsers",
    requestFormat: "json",
    parameters: [
      {
        name: "query",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/users/update/:id",
    alias: "updateUserType",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateUserTypeDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
